import { useUpdateUserColumn, useUserColumns } from '@services/auth'
import { DeviceType } from '@services/constants'
import Box from '@shared/innmaxUI/Box'
import Button from '@shared/innmaxUI/Form/Button'
import Checkbox from '@shared/innmaxUI/Form/Checkbox'
import Modal from '@shared/innmaxUI/Modal'
import { Form, Tabs } from 'antd'
import { ModalProps } from 'antd/lib/modal'
import { pathOr } from 'ramda'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ModalBody, ModalFooter } from 'src/components/Modal'
import styled from 'styled-components'

interface Props extends ModalProps {
  updateSuccessful: () => void
  visible: boolean
  onCancel: (evt: React.MouseEvent<HTMLElement>) => void
}

function ColumnSettingsModal({
  updateSuccessful,
  visible,
  onCancel,
  ...props
}: Props) {
  const { t } = useTranslation()

  const { userColumns, loading, refetch } = useUserColumns()

  const [form] = Form.useForm()

  const [, handleUpdate] = useUpdateUserColumn({
    onSuccess: () => {
      updateSuccessful()
      refetch()
    },
  })

  useEffect(() => {
    if (visible) {
      form.resetFields()
    }
    // eslint-disable-next-line
  }, [visible])

  const handleSubmit = (e: any) => {
    form.validateFields().then(values => {
      const body: any = {}
      // eslint-disable-next-line
      Object.keys(values).map((value: string) => {
        const [type, name] = value.split('-')
        const key = name.split(/(?=[A-Z])/)
        let arrayKey: any
        if (key[0] === 'properties' || key[0] === 'extra') {
          arrayKey =
            key.length > 1
              ? `${key[0]}.${key[1].toLocaleLowerCase()}${key
                  .splice(2)
                  .join('')}`
              : key[0]
        } else {
          arrayKey = name
        }
        if (!body[type]) {
          body[type] = {}
        }
        body[type][arrayKey] = values[value]
      })
      return handleUpdate(body)
    })
  }

  const renderTabPane = (key: DeviceType) => {
    const deviceType: string = key
    const deviceColumns: any = pathOr({}, [deviceType], userColumns)
    const deviceColumnKeys = Object.keys(deviceColumns)
    return (
      <div>
        <Row>
          {deviceColumnKeys.map((key: string) => (
            <StyledBox key={`${deviceType}-${key}`}>
              <StyledFormItem
                name={`${deviceType}-${key}`}
                valuePropName="checked"
                initialValue={pathOr(false, [key], deviceColumns)}>
                <Checkbox
                  className="text-white"
                  style={{ alignItems: 'center' }}>
                  {t(
                    `${
                      deviceType === DeviceType.CHARGINGPOINT
                        ? 'chargingPoint'
                        : deviceType
                    }:${key}`
                  )}
                </Checkbox>
              </StyledFormItem>
            </StyledBox>
          ))}
        </Row>
      </div>
    )
  }

  return (
    <StyledModal
      open={visible}
      width={960}
      className="form-item--is-no-margin-bottom"
      title={t('dashboard:set device fields for showing')}
      closable={false}
      bodyStyle={{
        padding: 0,
      }}
      footer={false}
      loadingData={loading}
      {...props}>
      <StyledModalBody>
        <Form form={form}>
          <StyledTabs
            animated={true}
            items={[DeviceType.CHARGINGPOINT, DeviceType.CONNECTOR].map(
              deviceType => ({
                key: deviceType,
                label: t(`device:${deviceType}`),
                children: renderTabPane(deviceType),
              })
            )}
          />
        </Form>
      </StyledModalBody>
      <ModalFooter>
        <>
          <span></span>
          <span>
            <Button
              type="primary"
              ghost
              key="btn-cancel"
              size="small"
              onClick={onCancel}>
              {t('common:cancel')}
            </Button>
            <Button
              type="primary"
              size="small"
              key="btn-ok"
              className="ml-16"
              loading={loading}
              onClick={handleSubmit}>
              {t('common:save')}
            </Button>
          </span>
        </>
      </ModalFooter>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  .ant-modal-footer > div {
    button {
      width: 80px;
      height: 32px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 500;
      border: solid 1.3px ${({ theme }) => theme.primary};
      &:first-child {
        color: ${({ theme }) => theme.primary};
      }
      &:last-child {
        background-color: ${({ theme }) => theme.primary};
        color: #ffffff;
      }
    }
  }
`

const StyledFormItem = styled(Form.Item)`
  margin-left: 10px !important;
  margin-bottom: 0 !important;
  align-items: center;
  .ant-form-item-control {
    line-height: unset;
  }
  .ant-checkbox {
    top: unset;
  }
`

const Row = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  div:nth-last-child(1),
  div:nth-last-child(2),
  div:nth-last-child(3) {
    padding-bottom: 0;
  }
`

const StyledBox = styled(Box)`
  flex: 0 0 33.333%;
  padding-bottom: 24px;
  // border-left: 1px solid ${({ theme }) => theme.whisper};
  align-items: center;
`

const StyledTabs = styled(Tabs)`
  width: 0;
  min-width: 100%;

  .ant-tabs-bar {
    border-bottom: 1px solid #c0c0c0;
  }

  .ant-tabs-nav {
    margin-bottom: 0 !important;
  }

  .ant-tabs-content-holder {
    padding: 20px 0;
  }

  .ant-tabs-tab {
    text-align: center;
    color: #c0c0c0;
    min-width: 144px;

    .ant-tabs-tab-btn {
      margin: 0 auto;
    }
  }

  .ant-tabs-tab-active.ant-tabs-tab {
    color: ${p => p.theme.primary};
  }

  .ant-tabs-ink-bar {
    background: ${p => p.theme.primary};
  }

  .ant-tabs-content {
    padding-left: 40px;
    padding-right: 4px;
  }
`

const StyledModalBody = styled(ModalBody)`
  padding: 0 !important;

  form {
    display: flex;
    height: 100%;

    .ant-tabs-content.ant-tabs-content-top {
      height: 100%;
      overflow: auto;

      .ant-tabs-tabpane {
        height: 100%;
        padding-bottom: 15px;

        div {
          overflow: auto;
          min-height: 100%;
        }
      }
    }
  }
`

export default React.memo(ColumnSettingsModal)
